import {
  GridActionsCellItem,
  GridColumns,
  GridRenderCellParams,
  GridRowParams,
  GridValueFormatterParams
} from '@mui/x-data-grid-premium';
import { currencyFormatter } from 'helpers/currency-formatter';
import { useTranslation } from 'react-i18next';
import {
  filterOperators,
  numberFilterOperators,
  dateFilterOperators,
  booleanFilterOperators
} from '../../../../constants';
import { baseCell } from '../cells/base-cell';
import { booleanCell } from '../cells/boolean-cell';
import { currencyCell } from '../cells/currency-cell';
import TransportDocumentLineUpdateDialog from 'components/invoices/TransportDocument/detail/TransportDocumentLineUpdateDialog';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Tooltip } from '@mui/material';
import { isDisabled } from '@testing-library/user-event/dist/utils';
import { filter } from 'lodash';
import { Sort } from '@mui/icons-material';
import { dateCell } from '../cells/date-cell';

export function TransportDocumentLinesColumns(
  locked = false,
  headerInfo = true,
  onCloseUpdate?: () => void,
  handleCheck?: (id: any) => void,
  handleUncheck?: (id: any) => void,
  handleUpdateExcluded?: (id: any, excluded: boolean) => void,
  disableEdit?: boolean
): GridColumns {
  const { t } = useTranslation();

  const columns: GridColumns = [
    {
      field: 'checkbox',
      headerName: t('global.print'),
      width: 50,
      filterable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const { id } = params.row; // Supponendo che l'ID sia presente in ogni riga

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          if (event.target.checked) {
            handleCheck?.(id); // Chiama la funzione `handleCheck` con l'ID
          } else {
            handleUncheck?.(id); // Chiama la funzione `handleUncheck` con l'ID
          }
        };

        return (
          <input
            type="checkbox"
            disabled={locked} // Disabilita la checkbox se `locked` è true
            onChange={handleChange}
          />
        );
      }
    },
    {
      field: 'quantity',
      headerName: t(`global.quantity`),
      width: 80,
      type: 'number',
      filterOperators: numberFilterOperators,
      renderCell: baseCell
    }
  ];

  if (!handleUpdateExcluded) {
    columns.push(
      // 17/12/2024 : Divisione fra DDT e Fattura
      // Richiesta da Stefano l'eliminazione della colonna "importo fatturato"
      {
        field: 'amount',
        headerName: t(`entities.ddt.amount`),
        type: 'number',
        filterable: true,
        filterOperators: numberFilterOperators,
        sortable: true,
        width: 125,
        editable: !locked,
        valueFormatter: (params: GridValueFormatterParams) => {
          return currencyFormatter(params.value);
        },
        renderCell: currencyCell
      }
    );
  }

  columns.push(
    {
      field: 'expected_amount',
      headerName: t(`entities.ddt.expected_amount`),
      filterOperators: numberFilterOperators,
      type: 'number',
      width: 125,
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value);
      },
      renderCell: currencyCell
    },
    {
      field: 'document',
      headerName: t(`entities.ddt.ref`),
      width: 100,
      filterable: true,
      filterOperators,
      sortable: true,
      renderCell: baseCell
    },
    {
      // richiesta di Stefano per ROMA 2 : 18/12/2024
      field: 'notes',
      headerName: t(`entities.ddt.notes`),
      filterOperators,
      editable: !disableEdit,
      width: 200,
      renderCell: baseCell
    }
  );

  columns.push(
    ...[
      {
        field: 'product',
        headerName: t(`global.product`),
        width: 150,
        renderCell: baseCell
      },
      {
        field: 'description',
        headerName: t(`global.description`),
        width: 250,
        filterOperators,
        renderCell: baseCell
      },
      {
        field: 'contract',
        headerName: t(`global.contract`),
        filterOperators,
        width: 75,
        renderCell: baseCell
      },
      {
        field: 'supplier',
        headerName: t(`global.supplier`),
        width: 150,
        filterable: false,
        sortable: false,
        renderCell: baseCell
      },
      {
        field: 'warehouse',
        headerName: t(`global.warehouse`),
        width: 150,
        filterable: true,
        filterOperators,
        sortable: true,
        renderCell: baseCell
      },
      {
        field: 'product_batch',
        headerName: t(`entities.movement.product_batch`),
        width: 100,
        filterable: false,
        renderCell: baseCell
      },
      {
        field: 'expiration_date',
        headerName: t(`entities.movement.expiration_date`),
        width: 100,
        filterable: false,
        renderCell: dateCell
      },
      {
        field: 'date',
        headerName: t(`entities.ddt.movement_date`),
        filterOperators: dateFilterOperators,
        width: 110,
        renderCell: dateCell
      },
      {
        field: 'discounted',
        width: 50,
        headerName: t(`entities.movement.discounted`),
        filterOperators: booleanFilterOperators,
        renderCell: booleanCell(t)
      },
      {
        field: 'unit_price',
        headerName: t(`entities.movement.unit_price`),
        type: 'number',
        filterable: true,
        filterOperators: numberFilterOperators,
        sortable: true,
        width: 150,
        valueFormatter: (params: GridValueFormatterParams) => {
          return currencyFormatter(params.value);
        },
        renderCell: currencyCell
      },
      {
        field: 'order_code',
        headerName: t(`entities.movement.order_code`),
        filterOperators,
        width: 150,
        renderCell: baseCell
      }
    ]
  );

  if (handleUpdateExcluded && !disableEdit) {
    columns.push(
      // ROMA changes
      {
        field: 'excluded_movement',
        headerName: t('global.already_counted'),
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => {
          const actions = [];
          actions.push(
            <GridActionsCellItem
              key={1}
              icon={
                <Tooltip
                  placement="top"
                  title={
                    params.row.excluded_movement
                      ? t('global.excluded_movement')
                      : t('global.included_movement')
                  } // Forces the tooltip to appear above the object
                >
                  <div>
                    {params.row.excluded_movement ? (
                      <RemoveShoppingCartIcon color={'disabled'} />
                    ) : (
                      <ShoppingCartIcon color={'primary'} />
                    )}
                  </div>
                </Tooltip>
              }
              onClick={() => handleUpdateExcluded(params.row.id, !params.row.excluded_movement)}
              label="Edit"
            />
          );
          return actions;
        }
      }
    );
  }

  if (onCloseUpdate && !disableEdit) {
    columns.push({
      field: 'actions',
      type: 'actions',
      width: 75,
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        return [
          <TransportDocumentLineUpdateDialog
            key={980}
            ddtLineId={params.row.id}
            supplierId={params.row.supplier_id}
            warehouseId={params.row.warehouse_id}
            onClose={onCloseUpdate}
          />
        ];
      },
      filterable: false,
      sortable: false
    });
  }

  return columns;
}
